import { useState } from 'react'
import PublicHeader from '../../publicHeader'
import Overlay from '../../Components/Overlay'

import {
    useLocation,
} from 'react-router-dom'

import {
    useMutation,
} from '@tanstack/react-query'

import retrieveCandidateResultsMutation from './services/retrieve-candidate-results'

const BackgroundCheckResults = () => {
    const location = useLocation()
    const queryParams = new URLSearchParams(location.search)
    const key = queryParams.get('key') // still retrieved but not shown

    // State for last 4 digits of SSN
    const [lastFourDigits, setLastFourDigits] = useState('')
    const [error, setError] = useState('')

    const {
        error: retrieveCandidateResultsError,
        isPending: retrieveCandidateResultsIsPending,
        mutate: retrieveCandidateResults,
        data: retrieveCandidateResultsData,
    } = useMutation({
        mutationFn: retrieveCandidateResultsMutation,
    })

    // Handle input to allow only numbers and max 4 characters
    const handleChange = (e) => {
        const value = e.target.value
        if (value.length <= 4) {
            setLastFourDigits(value)
        }
        if (error) setError('')
    }

    const handleSubmit = (e) => {
        e.preventDefault()

        if (lastFourDigits.length !== 4) {
            setError('Please enter exactly 4 digits.')
            return
        }

        setError('')
        retrieveCandidateResults({
            key,
            lastFourDigitsOfSsn: lastFourDigits,
        })
    }

    return (
        <div className="h-screen flex flex-col bg-gray-50">
            <PublicHeader />

            <div className="flex flex-1 items-center justify-center px-6">
                <div className="w-full max-w-3xl bg-white rounded-2xl shadow-lg p-10">
                    {/* Secure Icon */}
                    <div className="flex items-center justify-center mb-8">
                        <div className="bg-green-100 text-green-600 rounded-full p-4 shadow-sm">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-8 w-8"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 11c0-1.105.895-2 2-2h4c1.105 0 2 .895 2 2v7c0 1.105-.895 2-2 2h-4c-1.105 0-2-.895-2-2v-7zM7 11V9a5 5 0 0110 0v2"
                                />
                            </svg>
                        </div>
                    </div>

                    {/* Title */}
                    <h1 className="text-3xl font-bold text-gray-800 text-center mb-5">
                        Access Your Background Check Report
                    </h1>

                    {/* Description */}
                    <p className="text-gray-600 text-center max-w-2xl mx-auto mb-10 text-lg">
                        {retrieveCandidateResultsData?.resultsUrl
                            ? 'Your report is ready.'
                            : (
                                <>
                                    To view your background check report, please verify your identity by
                                    entering the <span className="font-semibold">last 4 digits of your Social Security Number (SSN)</span>.
                                </>
                            )}
                    </p>

                    {/* Conditional UI */}
                    {!retrieveCandidateResultsData?.resultsUrl ? (
                        // ---- FORM ----
                        <form onSubmit={handleSubmit} className="max-w-lg mx-auto space-y-6">
                            <div>
                                <label
                                    htmlFor="lastFourSSN"
                                    className="block text-sm font-medium text-gray-700 mb-2"
                                >
                                    Last 4 digits of SSN
                                </label>
                                <input
                                    id="lastFourSSN"
                                    name="lastFourSSN"
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={4}
                                    value={lastFourDigits}
                                    onChange={handleChange}
                                    placeholder="••••"
                                    className={`w-full rounded-xl border px-6 py-3 text-center text-xl tracking-widest focus:ring-2 focus:outline-none ${error
                                        ? 'border-red-500 focus:ring-red-500'
                                        : 'border-gray-300 focus:ring-blue-500'
                                        }`}
                                />
                                {/* Inline error message */}
                                {error && (
                                    <p className="mt-2 text-sm text-red-600 font-medium">
                                        {error}
                                    </p>
                                )}
                            </div>

                            <div className="space-y-3">
                                <button
                                    type="submit"
                                    className="w-full bg-[#4f46e5] text-white text-lg font-semibold py-3 rounded-xl hover:bg-[#4338ca] transition-colors shadow-md"
                                >
                                    View Report
                                </button>

                                {/* Generic API error */}
                                {retrieveCandidateResultsError && (
                                    <p className="text-sm text-red-600 text-center font-medium bg-red-50 border border-red-200 rounded-lg px-4 py-2">
                                        Something went wrong while retrieving your results. Please double-check your information and try again.
                                    </p>
                                )}
                            </div>
                        </form>
                    ) : (
                        // ---- SUCCESS STATE ----
                        <div className="max-w-lg mx-auto space-y-4">
                            <button
                                onClick={() => window.open(retrieveCandidateResultsData.resultsUrl, '_blank', 'noopener,noreferrer')}
                                className="w-full bg-[#4f46e5] text-white text-lg font-semibold py-3 rounded-xl hover:bg-[#4338ca] transition-colors shadow-md"
                            >
                                View Report
                            </button>

                            <button
                                onClick={() => window.open(retrieveCandidateResultsData.fcraUrl, '_blank', 'noopener,noreferrer')}
                                className="w-full bg-[#4f46e5] text-white text-lg font-semibold py-3 rounded-xl hover:bg-[#4338ca] transition-colors shadow-md"
                            >
                                A Summary of Your Rights Under the FCRA
                            </button>
                        </div>
                    )}

                    {/* Privacy Note */}
                    {!retrieveCandidateResultsData?.resultsUrl && (
                        <p className="mt-8 text-xs text-gray-500 text-center">
                            Your information is used only to confirm your identity and is not stored.
                        </p>
                    )}
                </div>
            </div>

            {/* Overlay */}
            {retrieveCandidateResultsIsPending && <Overlay />}
        </div>
    )
}

export default BackgroundCheckResults
